import { useEffect, useRef, useState } from "react";

// packages
import { cn } from "../../lib/utils";

// icons
import {
	FaCalendarDays,
	FaChevronLeft,
	FaChevronRight,
	FaCircleXmark,
} from "react-icons/fa6";

// ui components
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

// components
import NibjarNepaliDate, {
	getDaysInBSMonth,
	START_YEAR,
	END_YEAR,
	type BSDateInput,
	type Language,
} from "../../global/nibjar-nepali-date/nibjar-nepali-converter";

// ─── Constants ────────────────────────────────────────────────────────────────

// Derived from the library — automatically correct when yearMonthDaysArray grows
const BS_START_YEAR = START_YEAR;
const BS_END_YEAR = END_YEAR;

// Computed once at module load
const DEFAULT_MIN_DATE: Date = new NibjarNepaliDate(
	BS_START_YEAR,
	0,
	1,
).toJsDate();
// Use getDaysInBSMonth for the actual last day of the last month (avoids hardcoding 30)
const DEFAULT_MAX_DATE: Date = new NibjarNepaliDate(
	BS_END_YEAR,
	11,
	getDaysInBSMonth(BS_END_YEAR, 11),
).toJsDate();

const DEFAULT_FORMAT_EN = "DD MMM, YYYY";
const DEFAULT_FORMAT_NP = "DD MMMM, YYYY";

const AD_MONTHS_LONG = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];
const AD_MONTHS_SHORT = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];
const AD_DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const AD_DAYS_LONG = [
	"Sunday",
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
];

const WEEK_LABELS_SHORT_EN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const WEEK_LABELS_SHORT_NP = ["आ", "सो", "म", "बु", "बि", "शु", "श"];

const DEVANAGARI_DIGITS = ["०", "१", "२", "३", "४", "५", "६", "७", "८", "९"];

// ─── Types ────────────────────────────────────────────────────────────────────

export type DateMode = "en" | "np";

export interface DatePickerResult {
	en: Date;
	np: BSDateInput;
}

/** Controls calendar selection behavior — "single" | "multiple" | "range" */
export type SelectionMode = "single" | "multiple" | "range";

/** "month" shows the day grid; "year" shows the year picker overlay */
type CalendarView = "month" | "year";

/** Per-cell display state — structured for future range/multi-select support */
interface DayCellState {
	isSelected: boolean;
	isToday: boolean;
	isDisabled: boolean;
	isInRange: boolean;
	isRangeStart: boolean;
	isRangeEnd: boolean;
}

/** Full coordinates for range hover state — year/month prevents cross-month bleed */
interface HoverCoords {
	year: number;
	month: number;
	day: number;
}

/** Result returned by onRangeChange — end is null while mid-selection */
export interface RangePickerResult {
	start: DatePickerResult;
	end: DatePickerResult | null;
}

interface ViewCoords {
	year: number;
	month: number;
}

// ─── Quick-range helpers ──────────────────────────────────────────────────────

function offsetDays(base: Date, days: number): Date {
	const d = new Date(base);
	d.setDate(d.getDate() + days);
	return d;
}

function offsetMonths(base: Date, months: number): Date {
	const d = new Date(base);
	d.setMonth(d.getMonth() + months);
	return d;
}

function midnight(d: Date): number {
	const c = new Date(d);
	c.setHours(0, 0, 0, 0);
	return c.getTime();
}

interface QuickRangeOption {
	label: string;
	getRange: () => { start: Date; end: Date };
}

const QUICK_RANGES: QuickRangeOption[] = [
	{
		label: "Today",
		getRange: () => {
			const t = new Date();
			return { start: t, end: t };
		},
	},
	{
		label: "Last 7 days",
		getRange: () => {
			const t = new Date();
			return { start: offsetDays(t, -6), end: t };
		},
	},
	{
		label: "This month",
		getRange: () => {
			const t = new Date();
			return {
				start: new Date(t.getFullYear(), t.getMonth(), 1),
				end: t,
			};
		},
	},
	{
		label: "Last month",
		getRange: () => {
			const t = new Date();
			const first = new Date(t.getFullYear(), t.getMonth() - 1, 1);
			const last = new Date(t.getFullYear(), t.getMonth(), 0);
			return { start: first, end: last };
		},
	},
	{
		label: "Last 3 months",
		getRange: () => {
			const t = new Date();
			return { start: offsetMonths(t, -3), end: t };
		},
	},
	{
		label: "Last 6 months",
		getRange: () => {
			const t = new Date();
			return { start: offsetMonths(t, -6), end: t };
		},
	},
];

// ─── AD date formatter ────────────────────────────────────────────────────────

/**
 * Formats a JS Date using NibjarNepaliDate-compatible tokens for Gregorian output.
 * Tokens: D DD | M MM MMM MMMM | YYYY YYY YY | d dd ddd
 * Use `\\` to escape token characters.
 */
function formatADDate(date: Date, formatStr: string): string {
	const d = date.getDate();
	const mo = date.getMonth();
	const y = date.getFullYear();
	const wd = date.getDay();

	return formatStr
		.replace(
			/((\\[MDYd])|D{1,2}|M{1,4}|Y{2,4}|d{1,3})/g,
			(match, _, escaped?: string) => {
				switch (match) {
					case "D":
						return d.toString();
					case "DD":
						return d.toString().padStart(2, "0");
					case "M":
						return (mo + 1).toString();
					case "MM":
						return (mo + 1).toString().padStart(2, "0");
					case "MMM":
						return AD_MONTHS_SHORT[mo];
					case "MMMM":
						return AD_MONTHS_LONG[mo];
					case "YY":
						return y.toString().slice(-2);
					case "YYY":
						return y.toString().slice(-3);
					case "YYYY":
						return y.toString();
					case "d":
						return wd.toString();
					case "dd":
						return AD_DAYS_SHORT[wd];
					case "ddd":
						return AD_DAYS_LONG[wd];
					default:
						return (escaped ?? "").replace("/", "");
				}
			},
		)
		.replace(/\\/g, "");
}

/** Converts a number to Devanagari digit string */
function toDevanagari(n: number): string {
	return n
		.toString()
		.split("")
		.map((ch) => DEVANAGARI_DIGITS[parseInt(ch, 10)])
		.join("");
}

// ─── View coordinate helpers ──────────────────────────────────────────────────

/** One-way projection of the canonical AD anchor onto the calendar being displayed */
function getViewFromDate(date: Date, mode: DateMode): ViewCoords {
	if (mode === "np") {
		const np = new NibjarNepaliDate(date);
		return { year: np.getYear(), month: np.getMonth() };
	}
	return { year: date.getFullYear(), month: date.getMonth() };
}

/** Day-of-month of the anchor, read in whichever calendar is currently displayed */
function getDayInMode(date: Date, mode: DateMode): number {
	if (mode === "np") {
		return new NibjarNepaliDate(date).getDate();
	}
	return date.getDate();
}

/**
 * Builds a concrete AD anchor from year/month/day expressed in the given calendar —
 * day is clamped to that month's length. Used by navigation so the anchor is always
 * constructed directly in the calendar being navigated, never via a round-trip
 * through the other system.
 */
function buildDateInMode(
	year: number,
	month: number,
	day: number,
	mode: DateMode,
): Date {
	if (mode === "np") {
		const clampedDay = Math.min(day, getDaysInBSMonth(year, month));
		return new NibjarNepaliDate(year, month, clampedDay).toJsDate();
	}
	const clampedDay = Math.min(day, getDaysInADMonth(year, month));
	return new Date(year, month, clampedDay);
}

// ─── Calendar math helpers ────────────────────────────────────────────────────

function getDaysInADMonth(year: number, month: number): number {
	return new Date(year, month + 1, 0).getDate();
}

function getBSMonthFirstWeekday(year: number, month: number): number {
	return new NibjarNepaliDate(year, month, 1).getDay();
}

function getADMonthFirstWeekday(year: number, month: number): number {
	return new Date(year, month, 1).getDay();
}

// ─── Navigation accessibility ─────────────────────────────────────────────────

/**
 * Returns true if any day in the given month falls within [minDate, maxDate].
 * Used to decide whether prev/next arrows should be enabled.
 */
function isMonthAccessible(
	year: number,
	month: number,
	mode: DateMode,
	minDate: Date,
	maxDate: Date,
): boolean {
	// Guard: reject NP years outside the library range without ever constructing
	// NibjarNepaliDate — prevents console.error spam from findPassedDays when
	// checking the adjacent month beyond the library boundary.
	if (mode === "np" && (year < BS_START_YEAR || year > BS_END_YEAR)) {
		return false;
	}

	try {
		const firstDay =
			mode === "en"
				? new Date(year, month, 1)
				: new NibjarNepaliDate(year, month, 1).toJsDate();
		const lastDay =
			mode === "en"
				? new Date(year, month + 1, 0)
				: new NibjarNepaliDate(
						year,
						month,
						getDaysInBSMonth(year, month),
					).toJsDate();
		return firstDay <= maxDate && lastDay >= minDate;
	} catch {
		return false;
	}
}

// ─── CalendarHeader ───────────────────────────────────────────────────────────

interface CalendarHeaderProps {
	year: number;
	month: number;
	mode: DateMode;
	language: Language;
	canGoPrev: boolean;
	canGoNext: boolean;
	onPrev: () => void;
	onNext: () => void;
	/** Clicking the month/year label switches to the year picker view */
	onLabelClick: () => void;
}

function CalendarHeader({
	year,
	month,
	mode,
	language,
	canGoPrev,
	canGoNext,
	onPrev,
	onNext,
	onLabelClick,
}: CalendarHeaderProps) {
	// Month+year label — language-aware for NP mode
	const label =
		mode === "np"
			? new NibjarNepaliDate(year, month, 1).format("MMMM YYYY", language)
			: `${AD_MONTHS_LONG[month]} ${year}`;

	return (
		<div className="flex items-center justify-between mb-2">
			{/* Previous month — disabled at lower boundary */}
			<button
				type="button"
				onClick={onPrev}
				disabled={!canGoPrev}
				className={cn(
					"flex items-center justify-center w-7 h-7 rounded transition-colors",
					canGoPrev
						? "cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground"
						: "cursor-not-allowed opacity-30",
				)}
				aria-label="Previous month"
			>
				<FaChevronLeft size={15} />
			</button>

			{/* Month/year label — clicking opens year picker */}
			<button
				type="button"
				onClick={onLabelClick}
				className="text-sm font-medium text-foreground hover:text-primary transition-colors cursor-pointer px-1 rounded"
				aria-label="Select year"
			>
				{label}
			</button>

			{/* Next month — disabled at upper boundary */}
			<button
				type="button"
				onClick={onNext}
				disabled={!canGoNext}
				className={cn(
					"flex items-center justify-center w-7 h-7 rounded transition-colors",
					canGoNext
						? "cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground"
						: "cursor-not-allowed opacity-30",
				)}
				aria-label="Next month"
			>
				<FaChevronRight size={15} />
			</button>
		</div>
	);
}

// ─── YearPicker ───────────────────────────────────────────────────────────────

interface YearPickerProps {
	/** The year currently being viewed in the calendar */
	currentYear: number;
	/** The year of the currently selected date (may differ from currentYear) */
	selectedYear: number | null;
	mode: DateMode;
	language: Language;
	minYear: number;
	maxYear: number;
	onSelectYear: (year: number) => void;
	onBack: () => void;
}

function YearPicker({
	currentYear,
	selectedYear,
	mode,
	language,
	minYear,
	maxYear,
	onSelectYear,
	onBack,
}: YearPickerProps) {
	const currentYearRef = useRef<HTMLButtonElement>(null);

	// Scroll the current year into view when the picker mounts
	useEffect(() => {
		currentYearRef.current?.scrollIntoView({
			block: "center",
			behavior: "instant",
		});
	}, []);

	const years = Array.from(
		{ length: maxYear - minYear + 1 },
		(_, i) => minYear + i,
	);

	return (
		<div>
			{/* Year picker header */}
			<div className="flex items-center justify-between mb-2">
				<button
					type="button"
					onClick={onBack}
					className="flex items-center justify-center w-7 h-7 rounded cursor-pointer text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
					aria-label="Back to calendar"
				>
					<FaChevronLeft size={15} />
				</button>
				<span className="text-sm font-medium text-foreground">
					Select Year
				</span>
				{/* Spacer to keep title centred */}
				<div className="w-7" aria-hidden />
			</div>

			{/* Scrollable year grid — 3 columns */}
			<div className="max-h-52 overflow-y-auto pr-0.5">
				<div className="grid grid-cols-3 gap-1">
					{years.map((year) => {
						const isCurrentView = year === currentYear;
						const isSelectedYear =
							selectedYear !== null &&
							year === selectedYear &&
							!isCurrentView;

						// Display year in Devanagari for NP mode with np language
						const yearLabel =
							mode === "np" && language === "np"
								? toDevanagari(year)
								: year.toString();

						return (
							<button
								key={year}
								ref={isCurrentView ? currentYearRef : undefined}
								type="button"
								onClick={() => onSelectYear(year)}
								className={cn(
									"h-8 rounded text-xs font-medium transition-colors cursor-pointer",
									isCurrentView
										? "bg-primary text-primary-foreground"
										: isSelectedYear
											? "border border-primary text-primary hover:bg-muted"
											: "text-foreground hover:bg-muted",
								)}
							>
								{yearLabel}
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
}

// ─── CalendarGrid ─────────────────────────────────────────────────────────────

interface CalendarGridProps {
	year: number;
	month: number;
	mode: DateMode;
	language: Language;
	selectedDate: Date | null;
	minDate: Date;
	maxDate: Date;
	onSelectDay: (day: number) => void;
	selectionMode?: SelectionMode;
	// Range
	hoverCoords?: HoverCoords | null;
	onDayHover?: (coords: HoverCoords | null) => void;
	rangeStart?: Date | null;
	rangeEnd?: Date | null;
	// Multiple
	multipleDates?: Date[];
}

function CalendarGrid({
	year,
	month,
	mode,
	language,
	selectedDate,
	minDate,
	maxDate,
	onSelectDay,
	selectionMode = "single",
	hoverCoords = null,
	onDayHover,
	rangeStart = null,
	rangeEnd = null,
	multipleDates = [],
}: CalendarGridProps) {
	const daysInMonth =
		mode === "np"
			? getDaysInBSMonth(year, month)
			: getDaysInADMonth(year, month);
	const firstWeekday =
		mode === "np"
			? getBSMonthFirstWeekday(year, month)
			: getADMonthFirstWeekday(year, month);

	// Pre-compute today for "isToday" highlight
	const todayEN = new Date();
	const todayNP = mode === "np" ? new NibjarNepaliDate() : null;

	// Pre-compute BS coords for min/max to avoid NibjarNepaliDate construction per cell
	const bsMinVal = (() => {
		if (mode !== "np") return 0;
		try {
			const np = new NibjarNepaliDate(minDate);
			return np.getYear() * 10000 + np.getMonth() * 100 + np.getDate();
		} catch {
			return 0;
		}
	})();
	const bsMaxVal = (() => {
		if (mode !== "np") return Infinity;
		try {
			const np = new NibjarNepaliDate(maxDate);
			return np.getYear() * 10000 + np.getMonth() * 100 + np.getDate();
		} catch {
			return Infinity;
		}
	})();

	// Resolve which day number is selected in this month/year
	let selectedDay: number | null = null;
	if (selectedDate) {
		try {
			if (mode === "en") {
				if (
					selectedDate.getFullYear() === year &&
					selectedDate.getMonth() === month
				) {
					selectedDay = selectedDate.getDate();
				}
			} else {
				const snp = new NibjarNepaliDate(selectedDate);
				if (snp.getYear() === year && snp.getMonth() === month) {
					selectedDay = snp.getDate();
				}
			}
		} catch {
			// Selected date is outside library range — ignore
		}
	}

	// Resolve rangeStart/rangeEnd day numbers for this month/year
	let rangeStartDay: number | null = null;
	let rangeEndDay: number | null = null;
	if (selectionMode === "range") {
		if (rangeStart) {
			try {
				if (mode === "en") {
					if (
						rangeStart.getFullYear() === year &&
						rangeStart.getMonth() === month
					) {
						rangeStartDay = rangeStart.getDate();
					}
				} else {
					const np = new NibjarNepaliDate(rangeStart);
					if (np.getYear() === year && np.getMonth() === month) {
						rangeStartDay = np.getDate();
					}
				}
			} catch {
				/* out of range — ignore */
			}
		}
		if (rangeEnd) {
			try {
				if (mode === "en") {
					if (
						rangeEnd.getFullYear() === year &&
						rangeEnd.getMonth() === month
					) {
						rangeEndDay = rangeEnd.getDate();
					}
				} else {
					const np = new NibjarNepaliDate(rangeEnd);
					if (np.getYear() === year && np.getMonth() === month) {
						rangeEndDay = np.getDate();
					}
				}
			} catch {
				/* out of range — ignore */
			}
		}
	}

	// Resolve multiple selected day numbers for this month/year
	const multipleSelectedDays = new Set<number>();
	if (selectionMode === "multiple" && multipleDates.length > 0) {
		for (const d of multipleDates) {
			try {
				if (mode === "en") {
					if (d.getFullYear() === year && d.getMonth() === month) {
						multipleSelectedDays.add(d.getDate());
					}
				} else {
					const np = new NibjarNepaliDate(d);
					if (np.getYear() === year && np.getMonth() === month) {
						multipleSelectedDays.add(np.getDate());
					}
				}
			} catch {
				/* out of range — ignore */
			}
		}
	}

	// Weekday header labels — NP mode respects language prop
	const weekLabels =
		mode === "en" || language === "en"
			? WEEK_LABELS_SHORT_EN
			: WEEK_LABELS_SHORT_NP;

	// Build flat cell array: null for empty leading slots, number for day
	const cells: (number | null)[] = [
		...Array<null>(firstWeekday).fill(null),
		...Array.from({ length: daysInMonth }, (_, i) => i + 1),
	];
	while (cells.length % 7 !== 0) cells.push(null);

	// Helper: is a day within the hover/range preview window?
	const isInRangeCheck = (day: number): boolean => {
		if (selectionMode !== "range" || !rangeStart) return false;

		// Determine the end boundary: committed rangeEnd OR hover preview
		let endDate: Date | null = rangeEnd;
		// year/month guard: only apply hover preview for the current grid — prevents
		// stale cross-month highlight when the user navigates away mid-selection
		if (
			!endDate &&
			hoverCoords &&
			hoverCoords.year === year &&
			hoverCoords.month === month
		) {
			// Convert hoverCoords to a Date for comparison
			try {
				endDate =
					mode === "en"
						? new Date(
								hoverCoords.year,
								hoverCoords.month,
								hoverCoords.day,
							)
						: new NibjarNepaliDate(
								hoverCoords.year,
								hoverCoords.month,
								hoverCoords.day,
							).toJsDate();
			} catch {
				return false;
			}
		}
		if (!endDate) return false;

		// Convert this cell to a Date for comparison
		let cellDate: Date;
		try {
			cellDate =
				mode === "en"
					? new Date(year, month, day)
					: new NibjarNepaliDate(year, month, day).toJsDate();
		} catch {
			return false;
		}

		const lo =
			rangeStart.getTime() < endDate.getTime() ? rangeStart : endDate;
		const hi =
			rangeStart.getTime() < endDate.getTime() ? endDate : rangeStart;
		return (
			cellDate.getTime() > lo.getTime() &&
			cellDate.getTime() < hi.getTime()
		);
	};

	return (
		<div>
			{/* Weekday header row */}
			<div className="grid grid-cols-7 mb-1">
				{weekLabels.map((lbl, i) => (
					<div
						key={i}
						className="text-center text-xs text-muted-foreground font-medium py-1"
					>
						{lbl}
					</div>
				))}
			</div>

			{/* Day grid — fixed min-height prevents popover resize when navigating months */}
			<div className="grid grid-cols-7 gap-y-0.5 min-h-50.5 content-start">
				{cells.map((day, idx) => {
					if (day === null) {
						return <div key={`pad-${idx}`} aria-hidden />;
					}

					// Determine if this day falls outside the min/max bounds
					const isDisabled = (() => {
						try {
							if (mode === "en") {
								const t = new Date(year, month, day).getTime();
								return (
									t < minDate.getTime() ||
									t > maxDate.getTime()
								);
							} else {
								const bsVal = year * 10000 + month * 100 + day;
								return bsVal < bsMinVal || bsVal > bsMaxVal;
							}
						} catch {
							return true;
						}
					})();

					const isToday =
						mode === "en"
							? todayEN.getFullYear() === year &&
								todayEN.getMonth() === month &&
								todayEN.getDate() === day
							: todayNP!.getYear() === year &&
								todayNP!.getMonth() === month &&
								todayNP!.getDate() === day;

					const isRangeStartCell =
						selectionMode === "range" && rangeStartDay === day;
					const isRangeEndCell =
						selectionMode === "range" && rangeEndDay === day;
					const isMultiSelected =
						selectionMode === "multiple" &&
						multipleSelectedDays.has(day);

					const isSelected =
						!isDisabled &&
						(selectionMode === "single"
							? selectedDay === day
							: selectionMode === "range"
								? isRangeStartCell || isRangeEndCell
								: isMultiSelected);

					const cellState: DayCellState = {
						isSelected,
						isToday,
						isDisabled,
						isInRange: !isDisabled && isInRangeCheck(day),
						isRangeStart: !isDisabled && isRangeStartCell,
						isRangeEnd: !isDisabled && isRangeEndCell,
					};

					return (
						<button
							key={day}
							type="button"
							disabled={cellState.isDisabled}
							onClick={() =>
								!cellState.isDisabled && onSelectDay(day)
							}
							onMouseEnter={() =>
								onDayHover?.({ year, month, day })
							}
							onMouseLeave={() => onDayHover?.(null)}
							aria-label={String(day)}
							aria-pressed={cellState.isSelected}
							className={cn(
								"flex items-center justify-center h-8 w-full rounded text-xs transition-colors",
								cellState.isDisabled
									? "text-[var(--app-text-muted-a40)] cursor-not-allowed"
									: cellState.isRangeStart ||
										  cellState.isRangeEnd
										? "bg-[var(--app-primary-a80)] text-primary-foreground font-medium cursor-pointer"
										: cellState.isSelected
											? "bg-primary text-primary-foreground font-medium cursor-pointer"
											: cellState.isInRange
												? "bg-[var(--app-primary-a15)] text-foreground cursor-pointer hover:bg-[var(--app-primary-a25)]"
												: cellState.isToday
													? "border border-primary text-primary font-medium hover:bg-muted cursor-pointer"
													: "text-foreground hover:bg-muted cursor-pointer",
							)}
						>
							{day}
						</button>
					);
				})}
			</div>
		</div>
	);
}

// ─── InputDatePicker props ────────────────────────────────────────────────────

interface InputDatePickerProps {
	value?: Date | null;
	onChange?: (result: DatePickerResult) => void;
	onClear?: () => void;
	/** Initial calendar mode. Managed internally after mount. */
	mode?: DateMode;
	/** Format string — defaults to "DD MMM, YYYY" (EN) or "DD MMMM, YYYY" (NP) */
	format?: string;
	language?: Language;
	placeholder?: string;
	disabled?: boolean;
	label?: string;
	error?: string;
	/** Earliest selectable date (AD). Defaults to NibjarNepaliDate library start (~Apr 1993). */
	minDate?: Date | null;
	/** Latest selectable date (AD). Defaults to NibjarNepaliDate library end (BS 2090/12/30). */
	maxDate?: Date | null;
	/** Show clear button inside trigger when a value is set. Default: false */
	clearable?: boolean;
	/** Controls calendar selection behavior. Default: "single" */
	selectionMode?: SelectionMode;
	// Range mode
	rangeValue?: { start: Date | null; end: Date | null } | null;
	onRangeChange?: (result: RangePickerResult) => void;
	// Multiple mode
	multipleValue?: Date[];
	onMultipleChange?: (results: DatePickerResult[]) => void;
	containerClassName?: string;
	className?: string;
}

/** Derive DatePickerResult from a picked day in the current view */
function makeDatePickerResult(
	viewYear: number,
	viewMonth: number,
	day: number,
	mode: DateMode,
): DatePickerResult {
	let enDate: Date;
	let npDate: BSDateInput;
	if (mode === "en") {
		enDate = new Date(viewYear, viewMonth, day);
		const np = new NibjarNepaliDate(enDate);
		npDate = {
			year: np.getYear(),
			month: np.getMonth(),
			date: np.getDate(),
		};
	} else {
		const np = new NibjarNepaliDate(viewYear, viewMonth, day);
		enDate = np.toJsDate();
		npDate = {
			year: np.getYear(),
			month: np.getMonth(),
			date: np.getDate(),
		};
	}
	return { en: enDate, np: npDate };
}

/** Build a DatePickerResult from an arbitrary AD Date */
function makeDatePickerResultFromDate(date: Date): DatePickerResult {
	try {
		const np = new NibjarNepaliDate(date);
		return {
			en: date,
			np: {
				year: np.getYear(),
				month: np.getMonth(),
				date: np.getDate(),
			},
		};
	} catch {
		return { en: date, np: { year: 0, month: 0, date: 0 } };
	}
}

// ─── InputDatePicker ──────────────────────────────────────────────────────────

export default function InputDatePicker({
	value = null,
	onChange,
	onClear,
	mode = "np",
	format,
	language = "en",
	placeholder = "Select date",
	disabled = false,
	label,
	error,
	minDate = DEFAULT_MIN_DATE,
	maxDate = DEFAULT_MAX_DATE,
	clearable = false,
	selectionMode = "single",
	rangeValue = null,
	onRangeChange,
	multipleValue = [],
	onMultipleChange,
	containerClassName = "",
	className = "",
}: InputDatePickerProps) {
	const [open, setOpen] = useState(false);

	// Mode is managed internally — initialised from prop
	const [selectedMode, setSelectedMode] = useState<DateMode>(mode);

	// Calendar view: "month" shows the day grid, "year" shows the year picker
	const [calendarView, setCalendarView] = useState<CalendarView>("month");

	// Range hover — full coords prevent cross-month highlight bleed
	const [hoverCoords, setHoverCoords] = useState<HoverCoords | null>(null);

	// Effective bounds — fall back to library defaults when null is passed
	const effectiveMin = minDate ?? DEFAULT_MIN_DATE;
	const effectiveMax = maxDate ?? DEFAULT_MAX_DATE;

	// Canonical view anchor — always an AD Date, regardless of selectedMode. Both
	// calendars' displayed year/month are pure one-way derivations off this same
	// anchor (see viewYear/viewMonth below) so toggling language never needs to
	// convert or round-trip through the other calendar system.
	const [anchorDate, setAnchorDate] = useState<Date>(() => value ?? new Date());

	// Derived per-render — never stored, so it can't drift from anchorDate
	const { year: viewYear, month: viewMonth } = getViewFromDate(
		anchorDate,
		selectedMode,
	);

	// Per-mode default format if prop is not provided
	const resolvedFormat =
		format ??
		(selectedMode === "np" ? DEFAULT_FORMAT_NP : DEFAULT_FORMAT_EN);

	// Format selected date for the trigger display string
	const displayValue = (() => {
		if (selectionMode === "range") {
			const start = rangeValue?.start;
			const end = rangeValue?.end;
			if (!start) return "";
			const fmtDate = (d: Date) => {
				try {
					return selectedMode === "np"
						? new NibjarNepaliDate(d).format(
								resolvedFormat,
								language,
							)
						: formatADDate(d, resolvedFormat);
				} catch {
					return "";
				}
			};
			return end
				? `${fmtDate(start)} → ${fmtDate(end)}`
				: `${fmtDate(start)} → ...`;
		}

		if (selectionMode === "multiple") {
			if (multipleValue.length === 0) return "";
			// Fixed short format for compact display — DD MMM regardless of format prop
			const SHORT_FMT = "DD MMM";
			const sorted = [...multipleValue].sort(
				(a, b) => a.getTime() - b.getTime(),
			);
			const fmtShort = (d: Date) => {
				try {
					return selectedMode === "np"
						? new NibjarNepaliDate(d).format(SHORT_FMT, language)
						: formatADDate(d, SHORT_FMT);
				} catch {
					return "";
				}
			};
			const first2 = sorted.slice(0, 2).map(fmtShort).join(", ");
			const extra = sorted.length - 2;
			return extra > 0 ? `${first2}  +${extra} more` : first2;
		}

		// Single mode (existing)
		if (!value) return "";
		try {
			return selectedMode === "np"
				? new NibjarNepaliDate(value).format(resolvedFormat, language)
				: formatADDate(value, resolvedFormat);
		} catch {
			return "";
		}
	})();

	// Year of the selected value — used to highlight in year picker
	const selectedYear: number | null = (() => {
		let sourceDate: Date | null = null;
		if (selectionMode === "range") {
			sourceDate = rangeValue?.start ?? null;
		} else if (selectionMode === "multiple") {
			const sorted = [...multipleValue].sort(
				(a, b) => a.getTime() - b.getTime(),
			);
			sourceDate = sorted[0] ?? null;
		} else {
			sourceDate = value;
		}
		if (!sourceDate) return null;
		try {
			return selectedMode === "np"
				? new NibjarNepaliDate(sourceDate).getYear()
				: sourceDate.getFullYear();
		} catch {
			return null;
		}
	})();

	// Year range bounds for the year picker grid
	const yearPickerMin =
		selectedMode === "np" ? BS_START_YEAR : effectiveMin.getFullYear();
	const yearPickerMax =
		selectedMode === "np" ? BS_END_YEAR : effectiveMax.getFullYear();

	// Determine whether prev/next navigation arrows are accessible
	const prevMonthCoords =
		viewMonth === 0
			? { year: viewYear - 1, month: 11 }
			: { year: viewYear, month: viewMonth - 1 };
	const nextMonthCoords =
		viewMonth === 11
			? { year: viewYear + 1, month: 0 }
			: { year: viewYear, month: viewMonth + 1 };

	const canGoPrev = isMonthAccessible(
		prevMonthCoords.year,
		prevMonthCoords.month,
		selectedMode,
		effectiveMin,
		effectiveMax,
	);
	const canGoNext = isMonthAccessible(
		nextMonthCoords.year,
		nextMonthCoords.month,
		selectedMode,
		effectiveMin,
		effectiveMax,
	);

	// Toggle EN ↔ NP — the anchor is already canonical AD, so this is pure
	// re-projection: no date math. If a selection exists, re-center the anchor on
	// it (existing "jump to selection" behavior); otherwise the anchor — and thus
	// the viewed month — is left untouched across the toggle.
	const handleModeToggle = () => {
		const newMode: DateMode = selectedMode === "en" ? "np" : "en";

		// Resolve source date by selectionMode priority
		let sourceDate: Date | null = null;
		if (selectionMode === "range") {
			sourceDate = rangeValue?.start ?? null;
		} else if (selectionMode === "multiple") {
			const sorted = [...multipleValue].sort(
				(a, b) => a.getTime() - b.getTime(),
			);
			sourceDate = sorted[0] ?? null;
		} else {
			sourceDate = value;
		}

		if (sourceDate) setAnchorDate(sourceDate);
		setCalendarView("month");
		setHoverCoords(null);
		setSelectedMode(newMode);
	};

	// Month navigation — builds the new anchor directly as a concrete day in the
	// calendar being navigated (preserving the anchor's day-of-month, clamped),
	// never by converting through the other system.
	const handlePrevMonth = () => {
		if (!canGoPrev) return;
		const day = getDayInMode(anchorDate, selectedMode);
		setAnchorDate(
			buildDateInMode(
				prevMonthCoords.year,
				prevMonthCoords.month,
				day,
				selectedMode,
			),
		);
	};

	const handleNextMonth = () => {
		if (!canGoNext) return;
		const day = getDayInMode(anchorDate, selectedMode);
		setAnchorDate(
			buildDateInMode(
				nextMonthCoords.year,
				nextMonthCoords.month,
				day,
				selectedMode,
			),
		);
	};

	// Year selection from year picker — same month/day, new year, returns to month view
	const handleSelectYear = (year: number) => {
		const day = getDayInMode(anchorDate, selectedMode);
		setAnchorDate(buildDateInMode(year, viewMonth, day, selectedMode));
		setCalendarView("month");
	};

	// Day selection — branches on selectionMode
	const handleSelectDay = (day: number) => {
		const result = makeDatePickerResult(
			viewYear,
			viewMonth,
			day,
			selectedMode,
		);

		if (selectionMode === "single") {
			onChange?.(result);
			setOpen(false); // only single mode closes the popover
			return;
		}

		if (selectionMode === "range") {
			const start = rangeValue?.start ?? null;
			const end = rangeValue?.end ?? null;

			if (!start || end) {
				// No start yet OR complete range — reset, new click is new start
				onRangeChange?.({ start: result, end: null });
				return;
			}

			// Start is set, no end — determine position relative to start
			if (result.en.getTime() === start.getTime()) {
				// Same day as start — re-select start
				onRangeChange?.({ start: result, end: null });
			} else if (result.en.getTime() > start.getTime()) {
				// After start — complete the range; rebuild start as DatePickerResult
				let startResult: DatePickerResult;
				try {
					const np = new NibjarNepaliDate(start);
					startResult = {
						en: start,
						np: {
							year: np.getYear(),
							month: np.getMonth(),
							date: np.getDate(),
						},
					};
				} catch {
					// Unreachable in practice: CalendarGrid prevents selecting out-of-BS-range dates
					startResult = {
						en: start,
						np: { year: 0, month: 0, date: 0 },
					};
				}
				onRangeChange?.({ start: startResult, end: result });
			} else {
				// Before start — new start
				onRangeChange?.({ start: result, end: null });
			}
			return;
		}

		if (selectionMode === "multiple") {
			// Toggle: add or remove
			const alreadyIdx = multipleValue.findIndex(
				(d) =>
					d.getFullYear() === result.en.getFullYear() &&
					d.getMonth() === result.en.getMonth() &&
					d.getDate() === result.en.getDate(),
			);
			let updatedDates: Date[];
			if (alreadyIdx >= 0) {
				updatedDates = multipleValue.filter((_, i) => i !== alreadyIdx);
			} else {
				updatedDates = [...multipleValue, result.en];
			}
			updatedDates.sort((a, b) => a.getTime() - b.getTime());
			const updatedResults: DatePickerResult[] = updatedDates.map((d) => {
				const np = new NibjarNepaliDate(d);
				return {
					en: d,
					np: {
						year: np.getYear(),
						month: np.getMonth(),
						date: np.getDate(),
					},
				};
			});
			onMultipleChange?.(updatedResults);
		}
	};

	// Clear selected date — delegates to parent
	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation();
		onClear?.();
		// Multiple mode convenience — also fire the change handler with empty array
		if (selectionMode === "multiple") {
			onMultipleChange?.([]);
		}
	};

	// Today button — selects today, navigates view, keeps popover open
	const handleToday = () => {
		const today = new Date();
		// Navigate view to today's month — anchor is already canonical AD
		setAnchorDate(today);

		// Derive both EN and NP for today
		const np = new NibjarNepaliDate(today);
		const todayResult: DatePickerResult = {
			en: today,
			np: {
				year: np.getYear(),
				month: np.getMonth(),
				date: np.getDate(),
			},
		};

		if (selectionMode === "single") {
			onChange?.(todayResult);
		} else if (selectionMode === "range") {
			// Today always becomes new start (complete range resets per spec step 4)
			onRangeChange?.({ start: todayResult, end: null });
		} else {
			// Multiple: add today if not already present
			const alreadySelected = multipleValue.some(
				(d) =>
					d.getFullYear() === today.getFullYear() &&
					d.getMonth() === today.getMonth() &&
					d.getDate() === today.getDate(),
			);
			if (!alreadySelected) {
				const updated = [...multipleValue, today].sort(
					(a, b) => a.getTime() - b.getTime(),
				);
				const updatedResults: DatePickerResult[] = updated.map((d) => {
					const npd = new NibjarNepaliDate(d);
					return {
						en: d,
						np: {
							year: npd.getYear(),
							month: npd.getMonth(),
							date: npd.getDate(),
						},
					};
				});
				onMultipleChange?.(updatedResults);
			}
		}
	};

	// Quick range selection — fires onRangeChange and closes popover
	const handleQuickRange = (start: Date, end: Date) => {
		onRangeChange?.({
			start: makeDatePickerResultFromDate(start),
			end: makeDatePickerResultFromDate(end),
		});
		setOpen(false);
	};

	// Visible quick ranges — filtered to effectiveMin/effectiveMax
	const visibleQuickRanges =
		selectionMode === "range"
			? QUICK_RANGES.filter((r) => {
					const { start, end } = r.getRange();
					return (
						midnight(start) >= midnight(effectiveMin) &&
						midnight(end) <= midnight(effectiveMax)
					);
				})
			: [];

	// Today disabled if today falls outside effective min/max
	const todayAD = new Date();
	const isTodayDisabled =
		todayAD.getTime() < effectiveMin.getTime() ||
		todayAD.getTime() > effectiveMax.getTime();

	// Reset to month view whenever the popover closes
	const handleOpenChange = (next: boolean) => {
		if (!next) {
			setCalendarView("month");
			setHoverCoords(null);
		}
		if (!disabled) setOpen(next);
	};

	return (
		<div className={cn("flex items-end gap-2 min-w-32", className)}>
			{/* ── Input trigger + calendar popover ───────────────── */}
			<div className="flex flex-col flex-1">
				{label && (
					// Label click opens the calendar
					<label
						className="text-xs text-[var(--app-text-muted)] mb-1 cursor-pointer"
						onClick={() => !disabled && setOpen(true)}
					>
						{label}
					</label>
				)}

				<div className="flex gap-2">
					<Popover open={open} onOpenChange={handleOpenChange}>
						<PopoverTrigger asChild>
							{/* Input-style trigger — consistent with InputText */}
							<div
								role="button"
								tabIndex={disabled ? -1 : 0}
								onKeyDown={(e) => {
									if (e.key === "Enter" || e.key === " ")
										setOpen(true);
								}}
								className={cn(
									"flex-1 flex items-center gap-2 h-8 px-2 border rounded-sm bg-[var(--app-input-bg)] text-foreground transition-colors cursor-pointer select-none",
									open && !error
										? "border-primary"
										: "border-[var(--app-border-a40)]",
									error && "border-destructive",
									disabled &&
										"opacity-50 cursor-not-allowed pointer-events-none",
									containerClassName,
								)}
							>
								{/* Calendar icon */}
								<FaCalendarDays
									size={13}
									className="text-muted-foreground shrink-0"
								/>

								{/* Display value or placeholder */}
								<span
									className={cn(
										"flex-1 text-xs whitespace-nowrap",
										!displayValue &&
											"text-muted-foreground",
									)}
								>
									{displayValue || placeholder}
								</span>

								{/* Clear button — only rendered when clearable=true and a value is set */}
								{clearable &&
									(value ||
										(selectionMode === "range" &&
											(rangeValue?.start ||
												rangeValue?.end)) ||
										(selectionMode === "multiple" &&
											multipleValue.length > 0)) && (
										<button
											type="button"
											onClick={handleClear}
											className="flex items-center cursor-pointer"
											aria-label="Clear date"
										>
											<FaCircleXmark
												size={13}
												className="text-muted-foreground hover:text-foreground transition-colors"
											/>
										</button>
									)}
							</div>
						</PopoverTrigger>

						{/* ── Calendar popover ──────────────────────────── */}
						<PopoverContent className="w-72 p-3" align="start">
							{calendarView === "year" ? (
								// ── Year picker view ──────────────────────────
								<YearPicker
									currentYear={viewYear}
									selectedYear={selectedYear}
									mode={selectedMode}
									language={language}
									minYear={yearPickerMin}
									maxYear={yearPickerMax}
									onSelectYear={handleSelectYear}
									onBack={() => setCalendarView("month")}
								/>
							) : (
								// ── Month/day view ────────────────────────────
								<>
									{/* Quick range chips — range mode only */}
									{selectionMode === "range" &&
										visibleQuickRanges.length > 0 && (
											<>
												<div className="mb-2">
													<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
														Quick Ranges
													</p>
													<div className="flex flex-wrap gap-1">
														{visibleQuickRanges.map(
															(r) => {
																const {
																	start,
																	end,
																} =
																	r.getRange();
																const isActive =
																	rangeValue?.start &&
																	rangeValue?.end &&
																	midnight(
																		rangeValue.start,
																	) ===
																		midnight(
																			start,
																		) &&
																	midnight(
																		rangeValue.end,
																	) ===
																		midnight(
																			end,
																		);
																return (
																	<button
																		key={
																			r.label
																		}
																		type="button"
																		onClick={() =>
																			handleQuickRange(
																				start,
																				end,
																			)
																		}
																		className={cn(
																			"whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs transition-colors cursor-pointer",
																			isActive
																				? "border-primary bg-[var(--app-primary-a10)] text-primary"
																				: "border-border bg-muted text-foreground hover:border-primary hover:bg-[var(--app-primary-a10)]",
																		)}
																	>
																		{
																			r.label
																		}
																	</button>
																);
															},
														)}
													</div>
												</div>
												<div className="h-px bg-border mb-2" />
											</>
										)}

									{/* Header with clickable label → opens year picker */}
									<CalendarHeader
										year={viewYear}
										month={viewMonth}
										mode={selectedMode}
										language={language}
										canGoPrev={canGoPrev}
										canGoNext={canGoNext}
										onPrev={handlePrevMonth}
										onNext={handleNextMonth}
										onLabelClick={() =>
											setCalendarView("year")
										}
									/>

									{/* Day grid */}
									<CalendarGrid
										year={viewYear}
										month={viewMonth}
										mode={selectedMode}
										language={language}
										selectedDate={
											selectionMode === "single"
												? value
												: null
										}
										minDate={effectiveMin}
										maxDate={effectiveMax}
										onSelectDay={handleSelectDay}
										selectionMode={selectionMode}
										hoverCoords={hoverCoords}
										onDayHover={
											selectionMode === "range"
												? setHoverCoords
												: undefined
										}
										rangeStart={rangeValue?.start ?? null}
										rangeEnd={rangeValue?.end ?? null}
										multipleDates={multipleValue}
									/>
								</>
							)}

							{/* ── Today button footer ──────────────────────── */}
							{calendarView !== "year" && (
								<div className="border-t border-border pt-2 mt-2">
									<button
										type="button"
										onClick={handleToday}
										disabled={isTodayDisabled}
										className={cn(
											"text-xs font-medium px-2 py-1 rounded transition-colors",
											isTodayDisabled
												? "text-[var(--app-text-muted-a40)] cursor-not-allowed pointer-events-none"
												: "text-primary hover:bg-muted cursor-pointer",
										)}
									>
										{language === "np" ? "आज" : "Today"}
									</button>
								</div>
							)}
						</PopoverContent>
					</Popover>

					{/* ── EN/NP mode toggle pill ──────────────────────────
					Deliberately NOT gated by `disabled` — `disabled` only
					locks date selection (trigger + calendar), it shouldn't
					also block viewing the same, unchangeable value in the
					other calendar. */}
					<button
						type="button"
						onClick={handleModeToggle}
						aria-label={`Switch to ${selectedMode === "en" ? "Nepali" : "English"} calendar`}
						className={cn(
							"shrink-0 h-8 px-2 rounded-sm border text-xs font-semibold transition-colors cursor-pointer",
							selectedMode === "en"
								? "border-blue-400 bg-blue-50 text-blue-600 hover:bg-blue-100"
								: "border-red-400 bg-red-50 text-red-600 hover:bg-red-100",
						)}
					>
						{selectedMode === "en" ? "EN" : "NP"}
					</button>
				</div>

				{/* Inline error message */}
				{error && (
					<span className="text-[9px] text-destructive mt-0.5">
						{error}
					</span>
				)}
			</div>
		</div>
	);
}
